import { Box, Divider, Grid, GridItem, Text, VStack } from '@chakra-ui/react';
import type { AiModelContent, AiModelId } from '@/constants/aiLeague/aiLeagueTypes';
import type { AiLeagueChartUserInput, AiLeagueLeaderboardEntry, AiLeagueUserYouLine } from '@/hooks/useAiLeaguePageData';
import type { gameResult } from '@/models/gameUpdate';
import type { leagueMember } from '@/models/user';
import { pageSectionTitleProps } from '@/theme/sitePageTypography';
import { Card } from '@/views/LeaguePage/Card';
import { AiLeagueDesktopDashboard } from './AiLeagueDesktopDashboard';
import { AiLeagueHeader } from './AiLeagueHeader';
import { AiLeagueIntro } from './AiLeagueIntro';
import { AiLeagueLeaderboard } from './AiLeagueLeaderboard';
import { AiLeagueStandingsChartSection } from './AiLeagueStandingsChartSection';

export function AiLeagueTabPanel(props: {
  entries: AiLeagueLeaderboardEntry[];
  getMemberForRow: (entry: AiLeagueLeaderboardEntry) => leagueMember;
  tournamentStarted: boolean;
  settledMatchday: number;
  chartUser?: AiLeagueChartUserInput;
  aiModels: AiModelContent[];
  gameResults: gameResult[];
  leader: string | null;
  userYouLine: AiLeagueUserYouLine;
  userDisplayName: string | null;
  isLoggedIn: boolean;
  hasUserList: boolean;
  onModelClick: (modelId: AiModelId) => void;
  onMethodologyClick: () => void;
}) {
  const {
    entries,
    getMemberForRow,
    tournamentStarted,
    settledMatchday,
    chartUser,
    aiModels,
    gameResults,
    leader,
    userYouLine,
    userDisplayName,
    isLoggedIn,
    hasUserList,
    onModelClick,
    onMethodologyClick,
  } = props;

  let youLine: string | null = null;
  if (userYouLine != null) {
    if (typeof userYouLine === 'string') {
      youLine = userYouLine;
    } else {
      const label = userDisplayName ?? 'You';
      youLine = `${label}: ${userYouLine.rank} · ${userYouLine.score} pts`;
    }
  }

  const showHeader = youLine != null || (tournamentStarted && leader != null);
  const showChartLayout = settledMatchday >= 1;

  const leaderboardCard = (
    <Card w='full' borderRadius='md' p={{ base: 3, md: 4 }}>
      <Text {...pageSectionTitleProps} mb={2}>
        Leaderboard
      </Text>
      <AiLeagueLeaderboard
        entries={entries}
        getMemberForRow={getMemberForRow}
        tournamentStarted={tournamentStarted}
        showCompareCta={!hasUserList}
        isLoggedIn={isLoggedIn}
        onModelClick={onModelClick}
      />
    </Card>
  );

  const introCard = (
    <Card w='full' borderRadius='md' p={{ base: 3, md: 4 }}>
      <AiLeagueIntro onMethodologyClick={onMethodologyClick} />
    </Card>
  );

  const dashboardProps = {
    entries,
    getMemberForRow,
    tournamentStarted,
    settledMatchday,
    chartUser,
    aiModels,
    gameResults,
    isLoggedIn,
    hasUserList,
    onModelClick,
    onMethodologyClick,
  };

  return (
    <VStack align='stretch' spacing={6} w='full'>
      {showHeader ? (
        <AiLeagueHeader userYouLine={youLine} leader={leader} tournamentStarted={tournamentStarted} />
      ) : null}

      {showChartLayout ? (
        <>
          <Box display={{ base: 'none', lg: 'block' }} w='full'>
            <AiLeagueDesktopDashboard {...dashboardProps} />
          </Box>
          <Grid
            display={{ base: 'grid', lg: 'none' }}
            templateColumns='1fr'
            gap={4}
            w='full'
            alignItems='start'
          >
            <GridItem minW={0}>{leaderboardCard}</GridItem>
            <GridItem minW={0}>
              <AiLeagueStandingsChartSection
                gameResults={gameResults}
                settledMatchday={settledMatchday}
                aiModels={aiModels}
                chartUser={chartUser}
              />
            </GridItem>
            <GridItem minW={0}>{introCard}</GridItem>
          </Grid>
        </>
      ) : (
        <Card w='full' borderRadius='md' p={{ base: 3, md: 4 }}>
          <Text {...pageSectionTitleProps} mb={2}>
            Leaderboard
          </Text>
          <AiLeagueLeaderboard
            entries={entries}
            getMemberForRow={getMemberForRow}
            tournamentStarted={tournamentStarted}
            showCompareCta={!hasUserList}
            isLoggedIn={isLoggedIn}
            onModelClick={onModelClick}
          />
          <Divider my={5} />
          <AiLeagueIntro onMethodologyClick={onMethodologyClick} />
        </Card>
      )}
    </VStack>
  );
}
